import { createRouter, createWebHistory } from 'vue-router'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { checkTokenExpiration } from '@/services/auth.ts'
import Auth from '@/router/Auth'
import Admin from '@/router/Admin'
import Gallery from '@/router/Gallery'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: { name: 'gallery' },
    },
    {
      path: '/gallery',
      name: 'gallery',
      component: () => import('@/views/page/MainPageView.vue'),
      meta: { requiresAuth: true, role: ['admin', 'user'] },
      children: Gallery,
    },
    ...Admin,
    ...Auth,
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginPageView.vue'),
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: () => import('@/views/ChangePasswordView.vue'),
      meta: { requiresAuth: false, role: ['user', 'admin'] },
    },
    {
      path: '/gallery/asset/:page?/:pagesize?',
      name: 'gallery-asset',
      component: () => import('@/views/page/MainPageView.vue'),
      meta: { requiresAuth: true, role: ['admin', 'user'] },
    },
    {
      path: '/gallery/asset/cart/:page/:pagesize',
      name: 'cart-collection',
      component: () => import('@/views/page/MainPageView.vue'),
      meta: { requiresAuth: true, role: ['admin', 'user'] },
    },
    {
      path: '/gallery/asset/info/:id',
      name: 'asset-detail',
      component: () => import('@/views/page/MainPageView.vue'),
      meta: { requiresAuth: true, role: ['admin', 'user'] },
    },
    {
      path: '/admin/2fa',
      name: '2fa',
      component: () => import('@/views/TwoFactorAuthView.vue'),
      meta: {
        adminOnly: true,
      },
    },
    {
      path: '/admin/dashboard',
      name: 'admin-dashboard',
      component: () => import('@/views/admin/DashboardAdminView.vue'),
      meta: { requiresAuth: true, role: ['admin', 'user'], adminOnly: true },
    },
    {
      path: '/admin/user',
      name: 'admin-user',
      component: () => import('@/views/admin/AdminUsersView.vue'),
      meta: { requiresAuth: true, role: ['admin'], adminOnly: true },
    },
    {
      path: '/admin/user/create',
      name: 'admin-user-create',
      component: () => import('@/views/admin/UserCreateView.vue'),
      meta: { requiresAuth: true, role: ['admin'], adminOnly: true },
    },
    {
      path: '/admin/user/edit/:id',
      name: 'admin-user-edit',
      component: () => import('@/views/admin/UserEditView.vue'),
      meta: { requiresAuth: true, role: ['admin'], adminOnly: true },
    },
    {
      path: '/admin/user/profile',
      name: 'admin-user-profile',
      component: () => import('@/views/admin/UserEditView.vue'),
      meta: { requiresAuth: true, role: ['admin'], adminOnly: true },
    },
    {
      path: '/admin/page',
      name: 'admin-page',
      component: () => import('@/views/admin/AdminPagesView.vue'),
      meta: { requiresAuth: true, role: ['admin'], adminOnly: true },
    },
    {
      path: '/admin/page/create',
      name: 'admin-page-create',
      component: () => import('@/views/admin/PageCreateView.vue'),
      meta: { requiresAuth: true, role: ['admin'], adminOnly: true },
    },
    {
      path: '/admin/page/edit/:id',
      name: 'admin-page-edit',
      component: () => import('@/views/admin/PageEditView.vue'),
      meta: { requiresAuth: true, role: ['admin'], adminOnly: true },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: () => {
        const user: any = sessionStorage.getItem('user')
        if (user) {
          const { is_admin } = JSON.parse(user)
          if (is_admin) {
            return '/admin/dashboard'
          }
        }
        return '/gallery'
      },
      meta: { requiresAuth: true, role: ['user', 'admin'] },
    },
  ],
})

router.beforeEach(
  async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const isExpiredToken = checkTokenExpiration()
    let isAdmin: boolean = false
    const isAuthenticated = (): boolean =>
      (!!sessionStorage.getItem('token') && !isExpiredToken) || !!localStorage.getItem('token')
    const user = sessionStorage.getItem('user') || localStorage.getItem('user')
    if (user) {
      isAdmin = JSON.parse(user).is_admin
    }
    if (to.meta.requiresAuth && !isAuthenticated()) {
      next({ name: 'login' })
    } else {
      if ((to.path == '/gallery' || to.path == '/gallery/') && isAuthenticated()) {
        next({ name: 'gallery-asset', params: { page: 1, pagesize: 9 }, query: { activeNode: 0 } })
      }
      if (!isAdmin && to.meta?.adminOnly && isAuthenticated()) {
        next({ name: 'gallery-asset', params: { page: 1, pagesize: 9 }, query: { activeNode: 0 } })
      }
      next()
    }
  },
)
export default router
